// Die eine Wahrheit fuer abgeleitete Schiffswerte im Client (Gate FG-26).
//
// Wortgleiche Entsprechung von `backend/farmining/progression/stats.py`. Beide
// Seiten rechnen dasselbe: der Server, weil er den Abbau abrechnet; der Client,
// weil er ohne Rueckfrage zeichnen und offline spielen koennen muss.
//
// `computeStats` ist **rein**: gleiche Eingabe, gleiches Ergebnis, keine
// Nebenwirkung, keine Mutation der Grundwerte. Sie kennt den Spielstand nicht.

import {
  balancing, booster, enemy, node, nodes, perks, quests, sideQuests, xpForLevel,
} from '../data/balancing';
import type { DerivedStats, Recipe, StatMap, TreeNode } from '../data/types';
import { fmt, pct } from '../core/format';
import { projectPerks, type ColonyState } from './colony';

export interface SaveLike {
  levels: Record<string, number>;
  built: number;
  /** Wann welcher Booster **endet** — Sekunden seit der Epoche, kein Restwert. */
  boosterEnds: Record<string, number>;
  /** Wie hoch ein laufender Booster gestapelt ist — 1 ist der Normalfall. */
  boosterStacks: Record<string, number>;
  perks: Record<string, number>;
  quests: Record<string, boolean>;
  /** Eingelöste Nebenaufträge — ihr Bonus bleibt, wie der eines Auftrags. */
  sideClaimed: Record<string, boolean>;
  ship: { level: number };
  /** Die Kolonie (Akt III) — ihre abgeschlossenen Projekte wirken aufs Schiff. */
  colony?: ColonyState;
}

/** Kosten fuer die naechste Stufe eines Knotens. */
export function nodeCost(target: TreeNode, level: number): number {
  return Math.ceil(target.cost.base * Math.pow(target.cost.growth, level));
}

/** Die fertigen Ausbaustufen — sie werden strikt der Reihe nach gebaut. */
export function builtIds(built: number): Set<string> {
  return new Set(balancing().builds.slice(0, built).map((entry) => entry.id));
}

/**
 * Obergrenze einer Umwandlung — Hygiene, keine Lastbremse.
 *
 * Dieselbe Zahl steht als `MAX_CONVERT_RUNS` in `progression/rules.py` und
 * begrenzt dort die Eingabepruefung. Sie hier mitzufuehren kostet weniger als
 * die Alternative: schickte der Knopf „so oft wie moeglich" ungeklemmt,
 * antwortete der Server im Endspiel mit einem 400 statt mit einer Umwandlung.
 */
export const MAX_CONVERT_RUNS = 1_000_000;

/** Toleranz beim Abrunden — siehe `affordableRuns`. */
const RUN_EPS = 1e-9;

/**
 * Wie viele Durchlaeufe eines Rezepts der Vorrat deckt, hoechstens `wanted`.
 *
 * Die Solo-Fassung von `affordable_runs` (`progression/rules.py`). Sie liegt
 * hier und nicht beim Panel, weil **beide** Wege sie lesen — der Zug in
 * `net/moves.ts` und der Knopf in `ui/panels.ts` —, dieselbe Ueberlegung wie
 * bei der Freischalt- und Kostenlogik darueber.
 *
 * Online rechnet der Server dieselbe Zahl noch einmal und klemmt selbst. Das
 * ist keine zweite Regelquelle, sondern Autoritaet: zwischen Klick und Ankunft
 * kann sich der Vorrat geaendert haben.
 *
 * `2700 / 900` ist in Fliesskomma nicht zuverlaessig `3.0`; ohne die Toleranz
 * laege die dritte Portion fuer immer im Lager. Nach oben erfindet sie nichts
 * — dafuer steht die **Probe** am Ende: gezaehlt wird nur, was auch bezahlbar
 * ist.
 */
export function affordableRuns(
  res: Readonly<Record<string, number>>,
  recipe: Recipe,
  wanted: number,
): number {
  if (wanted <= 0) return 0;
  let moeglich = Math.min(wanted, MAX_CONVERT_RUNS);
  for (const [id, menge] of Object.entries(recipe.inputs)) {
    moeglich = Math.min(moeglich, Math.floor((res[id] ?? 0) / menge + RUN_EPS));
    if (moeglich <= 0) return 0;
  }
  const gedeckt = (n: number): boolean =>
    Object.entries(recipe.inputs).every(([id, menge]) => (res[id] ?? 0) >= menge * n);
  while (moeglich > 0 && !gedeckt(moeglich)) moeglich--;
  return moeglich;
}

export function nodeUnlocked(
  target: TreeNode,
  levels: Record<string, number>,
  built: ReadonlySet<string>,
  shipLevel: number,
  kills: Readonly<Record<string, number>> = {},
): boolean {
  if (!target.req.every((req) => (levels[req] ?? 0) > 0)) return false;
  if (target.needBuild && !built.has(target.needBuild)) return false;
  if (target.needLevel && shipLevel < target.needLevel) return false;
  // Ohne Angabe gilt „nichts erlegt" — der sichere Rückfall: ein Aufrufer, der
  // ihn vergisst, lässt einen Knoten gesperrt und nicht fälschlich offen.
  if (target.needKill && !(kills[target.needKill] ?? 0)) return false;
  return true;
}

/** Woran haengt es? Kommt aus **derselben** Auswertung wie die Sperre selbst. */
export function missingReasons(
  target: TreeNode,
  levels: Record<string, number>,
  built: ReadonlySet<string>,
  shipLevel: number,
  kills: Readonly<Record<string, number>> = {},
): string[] {
  const out: string[] = [];
  for (const req of target.req) {
    if (!(levels[req] ?? 0)) out.push(node(req)?.name ?? req);
  }
  if (target.needBuild && !built.has(target.needBuild)) out.push('Basis-Ausbau');
  if (target.needLevel && shipLevel < target.needLevel) out.push(`Schiffslevel ${target.needLevel}`);
  if (target.needKill && !(kills[target.needKill] ?? 0)) {
    out.push(enemy(target.needKill)?.name ?? target.needKill);
  }
  return out;
}

/** Alle Schiffswerte aus Grundwerten + gekauften Knoten. **Rein.** */
export function computeStats(levels: Record<string, number>): DerivedStats {
  const values: StatMap = { ...balancing().base, globalDamage: 1, splitBonus: 0 };
  const unlocks: Record<string, boolean> = {};

  const mulAcc: StatMap = {};
  const addAcc: StatMap = {};
  for (const entry of nodes()) {
    const level = levels[entry.id] ?? 0;
    if (!level) continue;
    if (entry.unlock) unlocks[entry.unlock] = true;
    if (entry.stat) {
      if (entry.mode === 'mul') mulAcc[entry.stat] = (mulAcc[entry.stat] ?? 0) + entry.per * level;
      else if (entry.mode === 'add') addAcc[entry.stat] = (addAcc[entry.stat] ?? 0) + entry.per * level;
    }
    for (const [key, value] of Object.entries(entry.extra)) {
      addAcc[key] = (addAcc[key] ?? 0) + value * level;
    }
  }
  for (const [key, acc] of Object.entries(mulAcc)) values[key] = (values[key] ?? 1) * (1 + acc);
  for (const [key, acc] of Object.entries(addAcc)) values[key] = (values[key] ?? 0) + acc;

  values.energyEff = Math.max(0.25, values.energyEff);
  values.bounceFalloff = Math.min(0.95, values.bounceFalloff);
  return { values, unlocks };
}

/**
 * Der **einzige** Einstiegspunkt fuer die fertigen Schiffswerte.
 *
 * `fleetPerk` ist der aktive Feld-Buff eines Flottenauftrags. Er kommt als
 * **Parameter** und nicht aus dem Spielstand: er gehoert zum Feld, nicht zum
 * Spieler, und die Funktion bleibt rein (Gate FG-26). Solo gibt es kein Feld
 * und nie einen Buff.
 *
 * `now` bestimmt, **welche Booster gerade wirken**. Auch das ist ein Parameter
 * und keine verborgene Uhr: derselbe Spielstand zur selben Zeit ergibt
 * dieselben Werte.
 */
export function derivedStats(
  save: SaveLike,
  fleetPerk?: StatMap | null,
  now: number = Date.now() / 1000,
): DerivedStats {
  const stats = computeStats(save.levels);
  const v = stats.values;

  // Jedes Schiffslevel gibt einen kleinen passiven Bonus.
  const levelBonus = save.ship.level - 1;
  v.globalDamage *= 1 + 0.03 * levelBonus;
  v.yieldMul *= 1 + 0.015 * levelBonus;

  // Basis-Ausbau: schnelleres Laden, der Sensorturm weitet die Sicht. Die
  // Kappung zaehlt die Stufen von Akt I vor dem Abschluss — seit der
  // Linsenkette sieben. Der Sensorturm bleibt die vierte Stufe: die neuen
  // beiden stehen **hinter** dem Kristalllabor.
  v.dockRegen *= 1 + 0.25 * Math.min(save.built, 7);
  if (save.built >= 4) v.sensor *= 1.12;

  applyPerks(v, save.perks);
  applyQuestPerks(v, save.quests);
  applySidePerks(v, save.sideClaimed);
  // Abgeschlossene Planetenprojekte wirken dauerhaft — dieselbe Karte, und
  // hier und nirgends sonst (FG-26). Wortgleich zu `_apply_project_perks`.
  if (save.colony) for (const perk of projectPerks(save.colony)) applyCard(v, perk);
  applyBoosters(
    v,
    Object.keys(save.boosterEnds).filter((id) => save.boosterEnds[id] > now),
    save.boosterStacks,
  );
  if (fleetPerk) applyCard(v, fleetPerk);
  return stats;
}

/**
 * Eine Wirkungskarte auflegen — Zielwert → Zuschlag, multiplikativ.
 *
 * **Die eine Bauform** für Auftragsboni, Feld-Buff und Booster. Gate FG-22
 * prüft ihre Schlüssel gegen die Grundwerte; ein erfundener Name wäre eine
 * Wirkung, die etwas kostet und nichts tut.
 */
function applyCard(v: StatMap, perk: StatMap): void {
  const additiv = new Set(balancing().additiveStats);
  for (const [stat, per] of Object.entries(perk)) {
    // **Multiplikativ — außer auf den Werten in `additiveStats`.** Die fangen
    // bei null an, und `1 + per` auf null bleibt null: die Karte wäre still
    // wirkungslos. Die Unterscheidung steht als Liste in der Tabelle und nicht
    // als „ist der Wert gerade null?" — `novaRate` steht ebenfalls auf null,
    // aber weil die Fähigkeit gesperrt ist, und dort ist `0 × 2 = 0` richtig.
    // **Wortgleich zu `_apply_card`.**
    if (additiv.has(stat)) v[stat] = (v[stat] ?? 0) + per;
    else v[stat] = (v[stat] ?? 1) * (1 + per);
  }
}

/** Die Level-Punkte. Sie wirken **hier** und nirgends sonst (Gate FG-26). */
function applyPerks(v: StatMap, spent: Record<string, number>): void {
  for (const entry of perks()) {
    const punkte = spent[entry.id] ?? 0;
    if (!punkte) continue;
    if (entry.mode === 'mul') v[entry.stat] = (v[entry.stat] ?? 1) * (1 + entry.per * punkte);
    else v[entry.stat] = (v[entry.stat] ?? 0) + entry.per * punkte;
  }
}

/**
 * Eingeloeste Auftraege wirken **dauerhaft**, nicht nur einmalig.
 *
 * Die Ressourcen helfen jetzt, der Bonus bleibt. Er steht in derselben
 * Tabellenzeile wie die Belohnung — nicht als zweite Liste daneben.
 */
function applyQuestPerks(v: StatMap, claimed: Record<string, boolean>): void {
  for (const entry of quests()) {
    if (claimed[entry.id]) applyCard(v, entry.perk);
  }
}

/**
 * Eingelöste **Nebenaufträge** wirken genauso dauerhaft wie Aufträge.
 *
 * Sie taten es nicht: `derivedStats` legte vier Karten auf — Level-Punkte,
 * Aufträge, Booster, Feld-Buff — und `save.sideClaimed` stand in keiner davon.
 * Alle fünfzig Ketten trugen einen `perk`, der Abzug transportierte ihn hierher,
 * und **keine Seite wandte ihn an**. Genau die Sorte Fehler, gegen die FG-22
 * steht, nur eine Ebene höher: eine Kette aus drei bis fünf Flügen quer durchs
 * Feld versprach einen Bonus, den niemand bekam, und nichts meldete sich.
 *
 * **Wortgleich zu `_apply_side_perks`.**
 */
function applySidePerks(v: StatMap, claimed: Record<string, boolean>): void {
  for (const entry of sideQuests()) {
    if (claimed[entry.id]) applyCard(v, entry.perk);
  }
}

/**
 * Die Wirkung der aktiven Booster — **aus der Tabelle**, nicht beim Namen.
 *
 * Welche gerade wirken, entscheidet der Aufrufer und reicht es herein: die
 * Statberechnung bleibt damit rein und kennt keine Uhr (Gate FG-26).
 */
/**
 * Die Wirkung der aktiven Booster. **Wortgleich zu `_apply_boosters`.**
 *
 * Ein Stapel wirkt so oft, wie er hoch ist: die Karte wird mehrfach aufgelegt
 * und nicht mit der Höhe multipliziert. `1 + per` zweimal ist `(1 + per)²`, und
 * genau das heißt „doppelt" bei einem Zuschlag — einmal mit `2 · per` wäre eine
 * andere Zahl.
 */
function applyBoosters(
  v: StatMap, active: readonly string[], stacks: Record<string, number> = {},
): void {
  for (const id of active) {
    const entry = booster(id);
    if (!entry) continue;
    const hoehe = Math.max(1, Math.min(balancing().boosterMaxStack, stacks[id] ?? 1));
    for (let i = 0; i < hoehe; i++) applyCard(v, entry.perk);
  }
}

/**
 * Wie viel Energie die laufenden Booster je Sekunde nachladen.
 *
 * Sie geht **nicht** über die Perk-Karte: eine Menge je Sekunde ist kein
 * Zuschlag auf einen Schiffswert. Sie geht denselben Weg wie die
 * Solarwind-Front und wird mit ihr addiert. **Wortgleich zu `booster_regen`.**
 */
export function boosterRegen(save: BoosterSave, now: number): number {
  let summe = 0;
  for (const [id, ende] of Object.entries(save.boosterEnds)) {
    if (ende <= now) continue;
    const entry = booster(id);
    if (!entry || entry.regen <= 0) continue;
    const hoehe = Math.max(1, Math.min(balancing().boosterMaxStack, save.boosterStacks[id] ?? 1));
    summe += entry.regen * hoehe;
  }
  return summe;
}

/** Nur das, was der Booster-Regen vom Spielstand liest. */
export interface BoosterSave {
  boosterEnds: Record<string, number>;
  boosterStacks: Record<string, number>;
}

/** Summe aller Bonuswerte eines Stats ueber den ganzen Baum. */
function statSum(stat: string, levels: Record<string, number>, mode: 'mul' | 'add'): number {
  let sum = 0;
  for (const entry of nodes()) {
    const level = levels[entry.id] ?? 0;
    if (!level) continue;
    if (entry.stat === stat && entry.mode === mode) sum += entry.per * level;
    if (mode === 'add' && entry.extra[stat] !== undefined) sum += entry.extra[stat] * level;
  }
  return sum;
}

/** Text fuer die Effektzeile im Tooltip: „100% → 105%". */
export function effectPreview(target: TreeNode, levels: Record<string, number>): string {
  if (target.mode === 'mul' && target.stat) {
    const current = 1 + statSum(target.stat, levels, 'mul');
    return `${pct(current)} → ${pct(current + target.per)}`;
  }
  if (target.mode === 'add' && target.stat) {
    const baseValue = balancing().base[target.stat] ?? 0;
    const current = baseValue + statSum(target.stat, levels, 'add');
    const next = current + target.per;
    if (target.asPct) return `${Math.round(current * 100)}% → ${Math.round(next * 100)}%`;
    return `${fmt(current)}${target.unit} → ${fmt(next)}${target.unit}`;
  }
  if (target.unlock) return 'schaltet frei';
  return '';
}

export { xpForLevel };

/**
 * Die Huelle fortschreiben — **wortgleich zu `progression/combat.tick_hull`**.
 *
 * Sie ist die zweite Reibungsgroesse neben der Energie: draussen regeneriert
 * sie langsam, in der Ladezone in Sekunden. Faellt sie auf null, wird das Schiff
 * zur Kapsel geschleppt — das kostet **Zeit**, nie Ressourcen, nie Erfahrung,
 * nie eine gekaufte Stufe (Gate FG-37).
 */
export function tickHull(
  hp: number,
  values: StatMap,
  incoming: number,
  distToBase: number,
  dt: number,
): { hp: number; lost: number; towed: boolean } {
  const hullMax = values.hullMax;
  const docked = distToBase < balancing().dockRange;

  const lost = Math.max(0, incoming) * armorFactor(values) * dt;
  const next = hp - lost;
  if (next <= 0) return { hp: hullMax, lost, towed: true };

  const regen = docked ? values.hullDockRegen : values.hullRegen;
  return { hp: Math.min(hullMax, next + regen * dt), lost, towed: false };
}

/** Wie viel Schaden die Panzerung durchlaesst. Gedeckelt: zaeh, nie unverwundbar. */
export function armorFactor(values: StatMap): number {
  const armor = Math.max(0, values.armor ?? 0);
  return Math.max(1 - balancing().armorCap, 1 - armor);
}
